import { useEffect } from "react"
import { useQuery, QueryKey, UseQueryOptions } from "react-query"
import { useAppDispatch } from "reducers/store"

const defaultQueryOptions: Omit<UseQueryOptions<any, unknown, any, QueryKey>, "queryKey" | "queryFn"> = {
  staleTime: 1000 * 60 * 2,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: true
}

export const useQueryState = <T,>(
  key: QueryKey,
  fn: (...args: unknown[]) => Promise<T>,
  reduxKey: string,
  options?: Omit<UseQueryOptions<T, unknown, T, QueryKey>, "queryKey" | "queryFn">
) => {
  const dispatch = useAppDispatch()

  const queryOptions = { ...defaultQueryOptions, ...options }

  const { data, isLoading, isError, error, refetch, remove, dataUpdatedAt } = useQuery(key, fn, queryOptions)

  useEffect(() => {
    if (isLoading) {
      dispatch({ type: `${reduxKey}_CALL` })
    }
  }, [isLoading])

  useEffect(() => {
    if (data) {
      dispatch({ type: `${reduxKey}_SUCCESS`, payload: data })
    }
  }, [data])

  useEffect(() => {
    if (isError) {
      dispatch({ type: `${reduxKey}_FAILURE`, payload: { error } })
    }
  }, [isError])

  const refetchAndBust = () => {
    remove()
    refetch()
  }

  return { refetch, refetchAndBust, dataUpdatedAt }
}

export const queryInvalidatorPredicate = (fetchKey: string) => (query: { queryKey: QueryKey }) => {
  const allQueryKeys = query.queryKey as QueryKey[]

  if (Array.isArray(allQueryKeys)) {
    return allQueryKeys.some((key) => {
      if (!key) return false

      if (typeof key !== "string") {
        if (typeof key === "object") {
          return JSON.stringify(key).includes(fetchKey)
        }

        return false
      }

      return key.includes(fetchKey)
    })
  } else {
    return (allQueryKeys as string)?.includes(fetchKey)
  }
}
